import type { ReactElement } from 'react';

/**
 * Beschriftetes Eingabefeld mit Zeichenzähler (§40.4).
 *
 * Bis S2 lag diese Komponente lokal in `VeranstaltungFormularSeite`. Mit dem Abstimmungsformular
 * und der Tokeneingabe braucht sie ein zweiter und dritter Ort — sie gehört damit zu den
 * gemeinsamen Bausteinen.
 *
 * Der sichtbare Zeichenzähler ist Absicht: eine Ablehnung wegen Überlänge soll nicht überraschen.
 * Verbindlich prüft der Server (§25.2); diese Anzeige dient allein der Benutzerführung.
 */
export function Feld({
  name,
  beschriftung,
  wert,
  grenze,
  pflicht,
  mehrzeilig,
  typ,
  hinweis,
  autoFocus,
  zeilen,
  onAendern,
}: {
  name: string;
  beschriftung: string;
  wert: string;
  grenze?: number;
  pflicht?: boolean;
  mehrzeilig?: boolean;
  typ?: string;
  /** Zusätzliche Erläuterung unterhalb des Feldes. */
  hinweis?: string;
  autoFocus?: boolean;
  zeilen?: number;
  onAendern: (wert: string) => void;
}): ReactElement {
  const gemeinsam = {
    id: name,
    name,
    value: wert,
    required: pflicht === true,
    ...(grenze === undefined ? {} : { maxLength: grenze }),
    ...(hinweis === undefined ? {} : { 'aria-describedby': `${name}-hinweis` }),
    className: 'border-rand bg-hintergrund rounded-md border px-3 py-2',
    onChange: (ereignis: { target: { value: string } }) => {
      onAendern(ereignis.target.value);
    },
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={name} className="text-sm font-medium">
        {beschriftung}
        {pflicht !== true && <span className="text-text-gedaempft"> (optional)</span>}
      </label>

      {mehrzeilig === true ? (
        <textarea {...gemeinsam} rows={zeilen ?? 4} autoFocus={autoFocus === true} />
      ) : (
        <input
          {...gemeinsam}
          type={typ ?? 'text'}
          autoFocus={autoFocus === true}
          className={`${gemeinsam.className} min-h-touchflaeche`}
        />
      )}

      {hinweis !== undefined && (
        <span id={`${name}-hinweis`} className="text-text-gedaempft text-xs">
          {hinweis}
        </span>
      )}

      {grenze !== undefined && (
        <span className="text-text-gedaempft text-xs">
          {wert.length} von {grenze} Zeichen
        </span>
      )}
    </div>
  );
}
